import { AlertCircle, Check, GitBranch } from "lucide-react";
import React from "react";
import { useLocalStorage } from "../hooks/useLocalStorage";
import {
	DEFAULT_REPO_PATHS,
	buildCheckoutCommand,
	copyCheckoutCommand,
} from "../utils";
import { Tooltip, TooltipBadgeContent } from "./Tooltip";

// How long the "copied"/"failed" feedback state stays visible (ms).
const FEEDBACK_DURATION_MS = 1500;

// Button that copies a `git checkout` command for the MR's source branch to the
// clipboard, using the repo -> local-path mapping configured in Settings.
// Hidden when the MR's repo has no configured local path or no source branch.
export const CopyCheckoutButton = ({ mr, className = "" }) => {
	const [repoPaths] = useLocalStorage("repoPaths", DEFAULT_REPO_PATHS);
	// "idle" | "copied" | "error"
	const [status, setStatus] = React.useState("idle");
	const timeoutRef = React.useRef(null);

	React.useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const command = buildCheckoutCommand(mr, repoPaths);
	// Nothing to do if the repo isn't configured or the branch is unknown.
	if (!command) return null;

	const handleClick = async (e) => {
		e.preventDefault();
		e.stopPropagation();

		const ok = await copyCheckoutCommand(mr, repoPaths);
		setStatus(ok ? "copied" : "error");

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		timeoutRef.current = setTimeout(() => {
			setStatus("idle");
			timeoutRef.current = null;
		}, FEEDBACK_DURATION_MS);
	};

	const tooltip =
		status === "copied"
			? { message: "Copied checkout command.", badge: "Done" }
			: status === "error"
				? { message: "Copy failed.", badge: "Error" }
				: { message: "Copy checkout command.", badge: "Git" };

	return (
		<Tooltip
			content={
				<TooltipBadgeContent badge={tooltip.badge}>
					{tooltip.message}
				</TooltipBadgeContent>
			}
		>
			<button
				type="button"
				onClick={handleClick}
				draggable="false"
				title={command}
				aria-label="Copy checkout command for this branch"
				className={`inline-flex h-6 w-6 items-center justify-center rounded hover:bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:text-[var(--color-link)] transition-colors ${className}`}
			>
				{status === "copied" ? (
					<Check className="w-4 h-4 text-green-500" />
				) : status === "error" ? (
					<AlertCircle className="w-4 h-4 text-red-500" />
				) : (
					<GitBranch className="w-4 h-4" />
				)}
			</button>
		</Tooltip>
	);
};

export default CopyCheckoutButton;
